/**
 * Utility function to apply filter criteria to a report configuration.
 * It translates the GAFilterSchema format into the GA4 Reporting API format.
 * @param {object} reportConfig - The base report template.
 * @param {Array<object>} filterCriteria - An array of filter criteria from your backend schema.
 * @returns {object} The complete report request object for the GA4 API.
 */
export const applyFiltersToReportConfig = (
  reportConfig,
  filterCriteria = [],
) => {
  // Initialize the filter expressions
  const dimensionFilterExpressions = [];
  const metricFilterExpressions = [];

  // Define a mapping for string operators to GA4 API's matchType
  const stringMatchTypes = {
    IN_LIST: 'IN_LIST',
    EQUALS: 'EXACT',
    CONTAINS: 'CONTAINS',
    BEGINS_WITH: 'BEGINS_WITH',
    ENDS_WITH: 'ENDS_WITH',
    MATCHES_REGEX: 'FULL_REGEXP', // Note: GA4 uses FULL_REGEXP for regex
  };

  // Define a mapping for numeric operators to GA4 API's operation
  const numericOperations = {
    EQUALS: 'EQUAL',
    GREATER_THAN: 'GREATER_THAN',
    LESS_THAN: 'LESS_THAN',
    GREATER_THAN_OR_EQUAL: 'GREATER_THAN_OR_EQUAL',
    LESS_THAN_OR_EQUAL: 'LESS_THAN_OR_EQUAL',
  };

  // Iterate over each filter criterion and build the corresponding API filter object
  filterCriteria.forEach((criterion) => {
    const { type, fieldName, operator, value } = criterion;

    if (type === 'dimension') {
      const matchType = stringMatchTypes[operator];
      if (!matchType) {
        console.error(`Invalid dimension operator: ${operator}`);
        return; // Skip this criterion
      }

      const filter = {
        fieldName: fieldName,
        stringFilter: {
          value: value,
          matchType: matchType,
        },
      };
      dimensionFilterExpressions.push({ filter });
    } else if (type === 'metric') {
      const operation = numericOperations[operator];
      if (!operation) {
        console.error(`Invalid metric operator: ${operator}`);
        return; // Skip this criterion
      }

      // The GA4 API uses `numericFilter` for metrics.
      // We parse the value to an integer for simplicity, but could handle floats too.
      // It's important to use the correct `int64Value` or `doubleValue` based on the metric.
      const filter = {
        fieldName: fieldName,
        numericFilter: {
          operation: operation,
          value: {
            int64Value: parseInt(value, 10) || 0, // Fallback to 0 if parsing fails
          },
        },
      };
      metricFilterExpressions.push({ filter });
    }
  });

  // Combine multiple filters using AND logic. This is the recommended practice.
  const dimensionFilter =
    dimensionFilterExpressions.length > 0
      ? { andGroup: { expressions: dimensionFilterExpressions } }
      : undefined;
  const metricFilter =
    metricFilterExpressions.length > 0
      ? { andGroup: { expressions: metricFilterExpressions } }
      : undefined;

  // Build the final report request object
  const finalReportRequest = {
    ...reportConfig,
    dimensionFilter,
    metricFilter,
  };

  return finalReportRequest;
};

const b64Encode = (str) => btoa(unescape(encodeURIComponent(str)));

const b64Decode = (b64) => decodeURIComponent(escape(atob(b64)));

const toUrlSafe = (b64) =>
  b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const fromUrlSafe = (safe) => {
  let b64 = safe.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  return b64;
};

export const ObjectParam = {
  encode: (obj) => {
    if (obj == null) return undefined;
    try {
      const json = JSON.stringify(obj);
      const b64 = b64Encode(json);
      return toUrlSafe(b64);
    } catch {
      return undefined;
    }
  },
  decode: (val) => {
    if (val == null) return undefined;
    try {
      const b64 = fromUrlSafe(val);
      const json = b64Decode(b64);
      return JSON.parse(json);
    } catch {
      return undefined;
    }
  },
  validate: (v) => typeof v === 'object' || v === undefined,
};
